import type {
    MergeRule,
    MergeRulesEvaluationContext,
    MergeRulesStandaloneConfiguration,
} from '@gitbook/api-client';
import { assertNever } from '@gitbook/util-ts';

import { MERGE_RULES, type MergeRuleDefinition } from './rules';

export type EvaluateMergeRuleResult = {
    rule: MergeRule;
    message: string;
};

export type EvaluateMergeRulesResult = {
    result: 'passed' | 'rejected';
    rejectedRules: EvaluateMergeRuleResult[];
    passedRules: EvaluateMergeRuleResult[];
    skippedRules: EvaluateMergeRuleResult[];
    context: MergeRulesEvaluationContext;
};

/**
 * Evaluate the merge rule against the given context
 */
export function evaluateMergeRules(input: {
    mergeRules: MergeRulesStandaloneConfiguration;
    context: MergeRulesEvaluationContext;
}): EvaluateMergeRulesResult {
    const { mergeRules, context } = input;

    switch (mergeRules.type) {
        case 'rules': {
            const rejectedRules: EvaluateMergeRuleResult[] = [];
            const passedRules: EvaluateMergeRuleResult[] = [];
            const skippedRules: EvaluateMergeRuleResult[] = [];
            let bypassed = false;

            // First pass: evaluate all non-bypass rules
            const nonBypassRules = mergeRules.rules.filter(
                (rule) => !MERGE_RULES[rule.rule].bypass
            );
            const bypassRules = mergeRules.rules.filter((rule) => MERGE_RULES[rule.rule].bypass);

            for (const ruleEntry of nonBypassRules) {
                const ruleDefinition = MERGE_RULES[ruleEntry.rule] as MergeRuleDefinition<
                    typeof ruleEntry.rule
                >;

                const ruleResult = ruleDefinition.evaluate(ruleEntry, context);

                if (ruleResult !== true) {
                    rejectedRules.push({
                        rule: ruleEntry,
                        message: typeof ruleResult === 'string' ? ruleResult : ruleDefinition.title,
                    });
                } else {
                    passedRules.push({
                        rule: ruleEntry,
                        message: ruleDefinition.title,
                    });
                }
            }

            // Second pass: only evaluate bypass rules if there are rejected non-bypass rules
            if (rejectedRules.length > 0) {
                for (const ruleEntry of bypassRules) {
                    const ruleDefinition = MERGE_RULES[ruleEntry.rule] as MergeRuleDefinition<
                        typeof ruleEntry.rule
                    >;

                    const ruleResult = ruleDefinition.evaluate(ruleEntry, context);

                    if (ruleResult !== true) {
                        rejectedRules.push({
                            rule: ruleEntry,
                            message:
                                typeof ruleResult === 'string' ? ruleResult : ruleDefinition.title,
                        });
                    } else {
                        passedRules.push({
                            rule: ruleEntry,
                            message: ruleDefinition.title,
                        });
                        bypassed = true;
                    }
                }
            } else {
                // If all non-bypass rules passed, add bypass rules to skipped rules
                for (const ruleEntry of bypassRules) {
                    const ruleDefinition = MERGE_RULES[ruleEntry.rule] as MergeRuleDefinition<
                        typeof ruleEntry.rule
                    >;
                    skippedRules.push({
                        rule: ruleEntry,
                        message: ruleDefinition.title,
                    });
                }
            }

            return {
                result: rejectedRules.length > 0 && !bypassed ? 'rejected' : 'passed',
                rejectedRules,
                passedRules,
                skippedRules,
                context,
            };
        }
        case 'none': {
            return {
                result: 'passed',
                rejectedRules: [],
                passedRules: [],
                skippedRules: [],
                context,
            };
        }
        default:
            assertNever(mergeRules);
    }
}
